// Alignova clear-aligner clinic pricing — the single source of truth for what a clinic pays.
//
// Tiered per tray (aligner step):
//   trays <= threshold  ->  rateAtOrBelow PKR / tray   (standard)
//   trays >  threshold  ->  rateAbove     PKR / tray   (bulk)
//
// The lab sets the threshold and both rates in Settings (they live on the settings row). The
// constants below are only the DEFAULTS a fresh install starts with, and what the client shows
// until the configured pricing has loaded. Every helper takes the pricing explicitly so nothing
// silently prices a case off stale defaults.
//
// Money everywhere in the app is stored as integer minor units (paisa; 100 per PKR), so the
// helpers return both rupee and paisa amounts.

export interface AlignerPricing {
  /** Trays at or below this count bill at the standard rate; above it, the bulk rate. */
  threshold: number;
  /** PKR per tray when trays <= threshold. */
  rateAtOrBelow: number;
  /** PKR per tray when trays > threshold. */
  rateAbove: number;
}

/** Defaults for a fresh install (and the client's placeholder until settings load). */
export const ALIGNER_PRICING: Readonly<AlignerPricing> = {
  threshold: 20,
  rateAtOrBelow: 2500,
  rateAbove: 2000,
};

const MINOR_UNITS_PER_PKR = 100;

export type AlignerTier = 'standard' | 'bulk';

export interface AlignerPriceBreakdown {
  trays: number;
  perTrayRupees: number;
  perTrayPaisa: number;
  totalRupees: number;
  totalPaisa: number;
  tier: AlignerTier;
}

/** The per-tray rate (in PKR) for a given tray count. */
export function alignerTrayRate(trays: number, pricing: AlignerPricing = ALIGNER_PRICING): number {
  return trays > pricing.threshold ? pricing.rateAbove : pricing.rateAtOrBelow;
}

/** Full breakdown of the clinic charge for a course of `trays` aligner trays. */
export function alignerTrayPrice(trays: number, pricing: AlignerPricing = ALIGNER_PRICING): AlignerPriceBreakdown {
  const n = Math.max(0, Math.trunc(Number.isFinite(trays) ? trays : 0));
  const perTrayRupees = alignerTrayRate(n, pricing);
  const perTrayPaisa = perTrayRupees * MINOR_UNITS_PER_PKR;
  return {
    trays: n,
    perTrayRupees,
    perTrayPaisa,
    totalRupees: n * perTrayRupees,
    totalPaisa: n * perTrayPaisa,
    tier: n > pricing.threshold ? 'bulk' : 'standard',
  };
}

/** One-line description of the tiers, for hints next to a tray input. */
export function alignerPricingHint(pricing: AlignerPricing): string {
  return `${pricing.threshold} trays or fewer: PKR ${pricing.rateAtOrBelow.toLocaleString()}/tray · above ${pricing.threshold}: PKR ${pricing.rateAbove.toLocaleString()}/tray`;
}

// ---------------------------------------------------------------------------------------------
// Discounts
// ---------------------------------------------------------------------------------------------

export const ALIGNER_DISCOUNT_TYPES = ['percent', 'amount'] as const;
export type AlignerDiscountType = (typeof ALIGNER_DISCOUNT_TYPES)[number];

/**
 * A discount agreed for one case's aligners: a percentage of the aligner price, or a flat PKR
 * amount off it. It applies to the aligner line only, never to an express-delivery surcharge.
 */
export interface AlignerDiscount {
  type: AlignerDiscountType;
  /** Percent (0-100) or whole PKR, depending on `type`. */
  value: number;
}

/** The discount stored on a case, if it amounts to anything. */
export function alignerDiscountOf(c: { discountType?: string | null; discountValue?: number | null }): AlignerDiscount | null {
  if (!c.discountType || c.discountValue == null || !(c.discountValue > 0)) return null;
  if (c.discountType !== 'percent' && c.discountType !== 'amount') return null;
  return { type: c.discountType, value: c.discountValue };
}

/** Paisa taken off an aligner charge of `grossPaisa`. Never more than the charge itself. */
export function alignerDiscountPaisa(grossPaisa: number, discount: AlignerDiscount | null | undefined): number {
  if (!discount || !(discount.value > 0) || grossPaisa <= 0) return 0;
  const raw =
    discount.type === 'percent'
      ? Math.round((grossPaisa * Math.min(100, discount.value)) / 100)
      : Math.round(discount.value * MINOR_UNITS_PER_PKR);
  return Math.min(grossPaisa, Math.max(0, raw));
}

/** "10%" or "PKR 2,000" - for the invoice line and the previews next to a tray count. */
export function alignerDiscountLabel(discount: AlignerDiscount): string {
  return discount.type === 'percent' ? `${discount.value}%` : `PKR ${discount.value.toLocaleString()}`;
}

export interface AlignerQuote {
  gross: AlignerPriceBreakdown;
  discount: AlignerDiscount | null;
  discountPaisa: number;
  discountRupees: number;
  netPaisa: number;
  netRupees: number;
}

/** What the clinic will actually be charged for the aligners: the tiered price less any discount. */
export function alignerQuote(trays: number, pricing: AlignerPricing, discount: AlignerDiscount | null | undefined): AlignerQuote {
  const gross = alignerTrayPrice(trays, pricing);
  const discountPaisa = alignerDiscountPaisa(gross.totalPaisa, discount);
  const netPaisa = gross.totalPaisa - discountPaisa;
  return {
    gross,
    discount: discountPaisa > 0 ? (discount ?? null) : null,
    discountPaisa,
    discountRupees: discountPaisa / MINOR_UNITS_PER_PKR,
    netPaisa,
    netRupees: netPaisa / MINOR_UNITS_PER_PKR,
  };
}
